// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { getCart } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base, Button, type DialogStateProps, Heading } from '@gandi/ui';

import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks/useAppSelector';

import type { Dispatch, GetState } from 'src/types';

import { addManyToCart } from '../../../modules/actions/cart';
import {
  selectOne,
  selectProvidedResultsByAvailability,
} from '../../../modules/selectors/results';

import type { ExtendedSuggestion } from '../../../types';
import { setModalState } from '../../PhasesDialog/module';

import { selectShowBulk } from '../modules';

import { AvailableDomains } from './AvailableDomains';

import Unavailable from './Unavailable';

import styles from './ProvidedDomainList.scss';

const addAllToCart =
  (suggestions: Array<string>) =>
  async (dispatch: Dispatch, getState: GetState): Promise<any> => {
    // $FlowFixMe[incompatible-type]
    const results: ExtendedSuggestion = suggestions.map(
      selectOne.bind(null, getState()),
    );

    // $FlowFixMe[prop-missing]
    const workableResults = results.filter(
      (suggestion: ExtendedSuggestion) =>
        suggestion.availability === 'available' &&
        suggestion.prices &&
        suggestion.prices.create &&
        !suggestion.error &&
        !suggestion.addedToCart,
    );

    const resultsNeedPhaseSelection = workableResults.filter(
      (suggestion: ExtendedSuggestion) =>
        (suggestion.prices?.create?.phases || []).length > 1,
    );

    try {
      await resultsNeedPhaseSelection.reduce(async (acc, current, i) => {
        await acc;
        await new Promise((resolve, reject) => {
          dispatch(
            // $FlowFixMe[not-an-array]
            setModalState({
              visible: true,
              phases: current.prices.create.phases,
              tld: current.tld,
              fqdn: current.fqdn,
              prices: current.prices,
              premium: current.premium,
              currency: current.currency,
              totalPage: resultsNeedPhaseSelection.length,
              currentPage: i + 1,
              callback: () => resolve(),
              callbackCancel: () => reject(),
            }),
          );
        });
      }, Promise.resolve());
      const resultsPhaseSelected = workableResults.map<ExtendedSuggestion>(
        ({ fqdn }) => selectOne(getState(), fqdn),
      );
      await dispatch(addManyToCart(resultsPhaseSelected));
    } catch (err) {
      // user has cancel operation
    }
  };

type Props = {
  phasesDialogState: DialogStateProps,
  isBulkLoading: boolean,
  setBulkLoading: React.ReactSetStateFunction<boolean>,
};

function AddAllButton({
  title,
  ...props
}: {
  loading: boolean,
  onClick: () => Promise<void>,
  title?: string,
}) {
  const { __ } = useTranslator();

  return (
    <Base
      flex
      {...(title ? { flexJustifyBetween: true } : { flexJustifyEnd: true })}
      margin="base"
    >
      {!!title && (
        <Heading as="h2" fontSize="l">
          {title}
        </Heading>
      )}
      <Button
        className={styles.button}
        iconBefore="cart"
        type="button"
        kind="link"
        intent="primary"
        {...props}
      >
        {__('Add all')}
      </Button>
    </Base>
  );
}
export function ProvidedDomainList({
  phasesDialogState,
  isBulkLoading,
  setBulkLoading,
}: Props): React.MixedElement | null {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { __ } = translator;

  const { available, unavailable } = useAppSelector(
    selectProvidedResultsByAvailability,
  );

  const showBulk = useAppSelector(selectShowBulk);

  const handleAddAllToCartClick: () => Promise<void> = async () => {
    setBulkLoading(true);

    try {
      const fqdns = available.map(({ fqdn }) => fqdn);
      const userBulkAction = dispatch(addAllToCart(fqdns));
      // fist time, we have to open the phases dialog
      phasesDialogState.show();
      // we are waiting for the user to finish to configure every domains
      await userBulkAction;
      await dispatch(getCart());
    } catch (err) {
      dispatch(notifyDanger(__('Unable to add the products in the cart.')));
    }
    setBulkLoading(false);
  };

  const showList = available.length > 0 || unavailable.length > 0;

  return showList ? (
    <>
      {showBulk && available.length > 2 && (
        <AddAllButton
          loading={isBulkLoading}
          onClick={handleAddAllToCartClick}
        />
      )}

      <Base margin="none" padding="none" className={styles.root}>
        <AvailableDomains phasesDialogState={phasesDialogState} />

        {showBulk && available.length > 2 && (
          <AddAllButton
            loading={isBulkLoading}
            onClick={handleAddAllToCartClick}
          />
        )}

        {unavailable.map(({ fqdn, error, availability }) => (
          <ErrorBoundary
            key={fqdn}
            fallback={<Unavailable.Error fqdn={fqdn} />}
          >
            <Unavailable
              error={error}
              fqdn={fqdn}
              availability={availability}
            />
          </ErrorBoundary>
        ))}
      </Base>
    </>
  ) : null;
}
